import type { OrderNode } from '../graphql/queries/orders';
import {
  decodeHtmlEntities,
  detectOrderType,
  metaArrayToRecord,
  parseOrderLineItemMeta,
  type TravelerRow,
} from './orderLineItemMeta';
import {
  formatOpsServiceDate,
  getOpsServiceStatusLabel,
  getOpsServiceTypeLabel,
  parseOpsServicesFromMeta,
  type OpsService,
} from './opsServices';

export type ReportKind = 'operations' | 'passengers' | 'summary';

export interface OperationReportRow {
  Section: string;
  'Booking #': string;
  Names: string;
  Pax: string;
  Hotel: string;
  'Arr/Dep': string;
  Tour: string;
  Guide: string;
  INC: string;
  OPT: string;
  Flight: string;
  From: string;
  To: string;
  'Flight Time': string;
  Date: string;
  'Pickup Time': string;
  Coordinator: string;
  Car: string;
  'Driver Name': string;
  Time: string;
}

export interface PassengerReportRow {
  No: number;
  'Booking #': string;
  Name: string;
  Nationality: string;
  'Date of Birth': string;
  'Weight (KG)': string;
  'ID / Passport': string;
  Gender: string;
  Mobile: string;
  Email: string;
}

/** One file-register style row per booking (from ops service popup fields). */
export interface SummaryReportRow {
  'Serial No': number;
  'Date of Booking Month': string;
  Code: string;
  'Booking Code': string;
  'Agency Name / Customer Name': string;
  Nationality: string;
  'PAX Conf.': string;
  Arrival: string;
  Departure: string;
  'Hotel Name': string;
  Status: string;
}

function normalizeYmd(value?: string | null): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isDateInRange(date: string, from: string, to: string): boolean {
  const d = normalizeYmd(date);
  const f = normalizeYmd(from);
  const t = normalizeYmd(to);
  if (!d || !f || !t) return false;
  return d >= f && d <= t;
}

function getCustomerName(order: OrderNode): string {
  const billing = order.billing;
  if (billing?.firstName || billing?.lastName) {
    return decodeHtmlEntities([billing.firstName, billing.lastName].filter(Boolean).join(' '));
  }
  return decodeHtmlEntities(order.customer?.email || billing?.email || '—');
}

function getBookingNumber(order: OrderNode): string {
  return `#${order.orderNumber ?? order.databaseId}`;
}

function collectLineItemDates(meta: Record<string, string>, orderDate?: string): string[] {
  const dates = new Set<string>();
  try {
    const pkg = meta.packageInfo ? JSON.parse(meta.packageInfo) : null;
    if (pkg && typeof pkg.date === 'string') {
      const n = normalizeYmd(pkg.date);
      if (n) dates.add(n);
    }
  } catch {
    /* ignore */
  }

  [
    meta['Booking Date'],
    meta.booking_date,
    meta._ngk_check_in,
    meta.tat_fields_arrival_date,
    meta.tat_fields_departure_date,
    meta._tat_arrival_date,
    meta._tat_departure_date,
    orderDate,
  ].forEach((c) => {
    const n = normalizeYmd(c);
    if (n) dates.add(n);
  });

  return Array.from(dates);
}

function collectOrderRelevantDates(order: OrderNode): string[] {
  const dates = new Set<string>();

  parseOpsServicesFromMeta(order.metaData).forEach((service) => {
    if (service.serviceDate) dates.add(service.serviceDate);
  });

  (order.lineItems?.nodes || []).forEach((item) => {
    collectLineItemDates(metaArrayToRecord(item.metaData), order.date).forEach((d) => dates.add(d));
  });

  const orderDate = normalizeYmd(order.date);
  if (orderDate) dates.add(orderDate);

  return Array.from(dates);
}

function pickDateInRange(dates: string[], from: string, to: string): string {
  return dates.find((d) => isDateInRange(d, from, to)) || '';
}

function paxFromMeta(meta: Record<string, string>): string {
  const adults = Number(meta.Adults || meta._tat_adults || 0);
  const children = Number(meta.Children || meta._tat_children || 0);
  const guests = Number(meta.Guests || 0);
  if (adults + children > 0) return String(adults + children);
  if (guests > 0) return String(guests);
  try {
    const pkg = meta.packageInfo ? JSON.parse(meta.packageInfo) : null;
    const total = Number(pkg?.totalAdults || 0) + Number(pkg?.totalChildren || 0);
    if (total > 0) return String(total);
  } catch {
    /* ignore */
  }
  return '';
}

function hotelFromMeta(meta: Record<string, string>): string {
  if (meta._ngk_hotel_name) return meta._ngk_hotel_name;
  if (meta['tat_fields_hotel_/_accommodation']) return meta['tat_fields_hotel_/_accommodation'];
  try {
    const pkg = meta.packageInfo ? JSON.parse(meta.packageInfo) : null;
    if (typeof pkg?.hotel_name === 'string' && pkg.hotel_name.trim()) return pkg.hotel_name.trim();
  } catch {
    /* ignore */
  }
  return '';
}

export function orderMatchesDateRange(order: OrderNode, from: string, to: string): boolean {
  return collectOrderRelevantDates(order).some((d) => isDateInRange(d, from, to));
}

function emptyOpRow(): OperationReportRow {
  return {
    Section: '',
    'Booking #': '',
    Names: '',
    Pax: '',
    Hotel: '',
    'Arr/Dep': '',
    Tour: '',
    Guide: '',
    INC: '',
    OPT: '',
    Flight: '',
    From: '',
    To: '',
    'Flight Time': '',
    Date: '',
    'Pickup Time': '',
    Coordinator: '',
    Car: '',
    'Driver Name': '',
    Time: '',
  };
}

function opsServiceToRow(order: OrderNode, service: OpsService): OperationReportRow {
  const isArrDep = service.serviceType === 'arrival' || service.serviceType === 'departure';
  const isTour = service.serviceType === 'tour';
  const base = emptyOpRow();

  return {
    ...base,
    Section: isArrDep ? 'Arrival-Departure' : isTour ? 'Tours' : getOpsServiceTypeLabel(service.serviceType),
    'Booking #': getBookingNumber(order),
    Names: getCustomerName(order),
    Pax: service.pax != null ? String(service.pax) : '',
    Hotel: service.hotel ? decodeHtmlEntities(service.hotel) : '',
    'Arr/Dep': isArrDep ? getOpsServiceTypeLabel(service.serviceType) : '',
    Tour: isTour ? decodeHtmlEntities(service.serviceName) : '',
    Guide: service.guide || '',
    INC: service.includedPax != null ? String(service.includedPax) : '',
    OPT: service.optionalPax != null ? String(service.optionalPax) : '',
    Flight: service.flightNumber || '',
    From: service.from || '',
    To: service.to || '',
    'Flight Time': service.flightTime || '',
    Date: formatOpsServiceDate(service.serviceDate),
    'Pickup Time': service.pickupTime || '',
    Coordinator: service.coordinator || '',
    Car: service.vehicle || '',
    'Driver Name': service.driver || '',
    Time: service.serviceTime || '',
  };
}

function bookingFallbackRows(order: OrderNode, from: string, to: string): OperationReportRow[] {
  const lineItems = order.lineItems?.nodes || [];
  const rows: OperationReportRow[] = [];

  lineItems.forEach((item) => {
    const meta = metaArrayToRecord(item.metaData);
    const productName = item.product?.node?.name || '';
    const matchDate = pickDateInRange(collectLineItemDates(meta, order.date), from, to);
    if (!matchDate) return;

    const type = detectOrderType(meta, productName);
    const isTour = type === 'tour';
    const tourName = meta['Tour Title'] || (isTour ? productName : '');

    rows.push({
      ...emptyOpRow(),
      Section: isTour ? 'Tours' : 'Arrival-Departure',
      'Booking #': getBookingNumber(order),
      Names: getCustomerName(order),
      Pax: paxFromMeta(meta),
      Hotel: decodeHtmlEntities(hotelFromMeta(meta)),
      Tour: isTour ? decodeHtmlEntities(tourName) : '',
      Date: formatOpsServiceDate(matchDate),
    });
  });

  if (rows.length) return rows;

  const orderDate = pickDateInRange(collectOrderRelevantDates(order), from, to);
  if (!orderDate) return [];

  return [
    {
      ...emptyOpRow(),
      Section: 'Arrival-Departure',
      'Booking #': getBookingNumber(order),
      Names: getCustomerName(order),
      Date: formatOpsServiceDate(orderDate),
    },
  ];
}

export function buildOperationReportRows(
  orders: OrderNode[],
  from: string,
  to: string
): OperationReportRow[] {
  const arrivalRows: OperationReportRow[] = [];
  const tourRows: OperationReportRow[] = [];
  const otherRows: OperationReportRow[] = [];

  const pushRow = (row: OperationReportRow) => {
    if (row.Section === 'Arrival-Departure') arrivalRows.push(row);
    else if (row.Section === 'Tours') tourRows.push(row);
    else otherRows.push(row);
  };

  orders.forEach((order) => {
    const opsInRange = parseOpsServicesFromMeta(order.metaData).filter(
      (service) => isDateInRange(service.serviceDate, from, to) && service.status !== 'cancelled'
    );

    if (opsInRange.length) {
      opsInRange.forEach((service) => pushRow(opsServiceToRow(order, service)));
      return;
    }

    if (!orderMatchesDateRange(order, from, to)) return;
    bookingFallbackRows(order, from, to).forEach(pushRow);
  });

  const sortByDateThenName = (a: OperationReportRow, b: OperationReportRow) => {
    const dateCmp = a.Date.localeCompare(b.Date);
    if (dateCmp !== 0) return dateCmp;
    return a.Names.localeCompare(b.Names);
  };

  arrivalRows.sort(sortByDateThenName);
  tourRows.sort(sortByDateThenName);
  otherRows.sort(sortByDateThenName);

  return [...arrivalRows, ...tourRows, ...otherRows];
}

function travelerContact(
  traveler: TravelerRow,
  order: OrderNode
): { phone: string; email: string } {
  const metaPhone =
    order.metaData?.find((m) => m.key === '_ngk_guest_phone')?.value ||
    order.billing?.phone ||
    '';
  const metaEmail =
    order.metaData?.find((m) => m.key === '_ngk_guest_email')?.value ||
    order.billing?.email ||
    order.customer?.email ||
    '';

  return {
    phone: traveler.phone || metaPhone || '',
    email: traveler.email || metaEmail || '',
  };
}

export function buildPassengerReportRows(
  orders: OrderNode[],
  from: string,
  to: string
): PassengerReportRow[] {
  const rows: PassengerReportRow[] = [];
  let no = 1;

  orders.forEach((order) => {
    if (!orderMatchesDateRange(order, from, to)) return;

    const bookingNo = getBookingNumber(order);
    const seen = new Set<string>();

    (order.lineItems?.nodes || []).forEach((item) => {
      const meta = metaArrayToRecord(item.metaData);
      const parsed = parseOrderLineItemMeta(meta, item.product?.node?.name || '');
      parsed.travelers.forEach((traveler) => {
        const key = `${traveler.name}|${traveler.dob || ''}|${traveler.passport || ''}`;
        if (seen.has(key)) return;
        seen.add(key);
        const contact = travelerContact(traveler, order);
        rows.push({
          No: no++,
          'Booking #': bookingNo,
          Name: decodeHtmlEntities(traveler.name),
          Nationality: traveler.nationality || '',
          'Date of Birth': traveler.dob || '',
          'Weight (KG)': traveler.weight || '',
          'ID / Passport': traveler.passport || '',
          Gender: traveler.gender || '',
          Mobile: contact.phone,
          Email: contact.email,
        });
      });
    });
  });

  return rows;
}

const BOOKING_MONTH_LABELS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
] as const;

function bookingMonthLabel(ymd: string): string {
  const normalized = normalizeYmd(ymd);
  if (!normalized) return '';
  const monthIndex = Number(normalized.slice(5, 7)) - 1;
  if (monthIndex < 0 || monthIndex > 11) return '';
  return BOOKING_MONTH_LABELS[monthIndex];
}

function firstFilled(services: OpsService[], pick: (s: OpsService) => string | number | null | undefined): string {
  for (const service of services) {
    const value = pick(service);
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

function maxPax(services: OpsService[]): string {
  let max: number | null = null;
  services.forEach((service) => {
    if (typeof service.pax === 'number' && !Number.isNaN(service.pax)) {
      max = max === null ? service.pax : Math.max(max, service.pax);
    }
  });
  return max === null ? '' : String(max);
}

function agencyOrCustomerName(order: OrderNode, services: OpsService[]): string {
  const agency = firstFilled(services, (s) => s.agency);
  const customer = getCustomerName(order);
  if (agency && customer && agency.toLowerCase() !== customer.toLowerCase()) {
    return `${agency} / ${customer}`;
  }
  return agency || customer || '';
}

function pickSummaryStatus(services: OpsService[]): string {
  const preferred =
    services.find((s) => s.serviceType === 'arrival' && s.status !== 'cancelled') ||
    services.find((s) => s.serviceType === 'departure' && s.status !== 'cancelled') ||
    services.find((s) => s.status !== 'cancelled') ||
    services[0];
  return preferred ? getOpsServiceStatusLabel(preferred.status) : '';
}

function summarizeBookingFromOps(order: OrderNode, services: OpsService[]): Omit<SummaryReportRow, 'Serial No'> {
  const active = services.filter((s) => s.status !== 'cancelled');
  const pool = active.length ? active : services;

  const arrivalYmd =
    firstFilled(
      pool.filter((s) => s.serviceType === 'arrival'),
      (s) => s.serviceDate
    ) || '';
  const departureYmd =
    firstFilled(
      pool.filter((s) => s.serviceType === 'departure'),
      (s) => s.serviceDate
    ) || '';

  const monthSource =
    arrivalYmd ||
    firstFilled(pool, (s) => s.serviceDate) ||
    normalizeYmd(order.date) ||
    '';

  const hotel =
    firstFilled(
      pool.filter((s) => s.serviceType === 'arrival' || s.serviceType === 'departure'),
      (s) => s.hotel
    ) || firstFilled(pool, (s) => s.hotel);

  return {
    'Date of Booking Month': bookingMonthLabel(monthSource),
    Code: firstFilled(pool, (s) => s.fileNo),
    'Booking Code': String(order.orderNumber ?? order.databaseId ?? ''),
    'Agency Name / Customer Name': agencyOrCustomerName(order, pool),
    Nationality: firstFilled(pool, (s) => s.nationality),
    'PAX Conf.': maxPax(pool),
    Arrival: arrivalYmd ? formatOpsServiceDate(arrivalYmd) : '',
    Departure: departureYmd ? formatOpsServiceDate(departureYmd) : '',
    'Hotel Name': decodeHtmlEntities(hotel),
    Status: pickSummaryStatus(pool),
  };
}

/**
 * Summary / file-register extract: one row per booking that has ops services
 * overlapping the date filter. Fields come from the booking service popup.
 */
export function buildSummaryReportRows(
  orders: OrderNode[],
  from: string,
  to: string
): SummaryReportRow[] {
  const drafted: Array<Omit<SummaryReportRow, 'Serial No'> & { _sort: string }> = [];

  orders.forEach((order) => {
    const services = parseOpsServicesFromMeta(order.metaData);
    if (!services.length) return;

    const inRange = services.some(
      (service) => isDateInRange(service.serviceDate, from, to) && service.status !== 'cancelled'
    );
    if (!inRange) return;

    const row = summarizeBookingFromOps(order, services);
    drafted.push({
      ...row,
      _sort:
        normalizeYmd(
          services.find((s) => s.serviceType === 'arrival')?.serviceDate ||
            services.find((s) => isDateInRange(s.serviceDate, from, to))?.serviceDate ||
            services[0]?.serviceDate
        ) || '9999-99-99',
    });
  });

  drafted.sort((a, b) => {
    const dateCmp = a._sort.localeCompare(b._sort);
    if (dateCmp !== 0) return dateCmp;
    return a['Booking Code'].localeCompare(b['Booking Code']);
  });

  return drafted.map(({ _sort: _ignored, ...row }, index) => ({
    'Serial No': index + 1,
    ...row,
  }));
}

export const OPERATION_CSV_HEADERS = [
  'Section',
  'Booking #',
  'Names',
  'Pax',
  'Hotel',
  'Arr/Dep',
  'Tour',
  'Guide',
  'INC',
  'OPT',
  'Flight',
  'From',
  'To',
  'Flight Time',
  'Date',
  'Pickup Time',
  'Coordinator',
  'Car',
  'Driver Name',
  'Time',
];

export const PASSENGER_CSV_HEADERS = [
  'No',
  'Booking #',
  'Name',
  'Nationality',
  'Date of Birth',
  'Weight (KG)',
  'ID / Passport',
  'Gender',
  'Mobile',
  'Email',
];

export const SUMMARY_CSV_HEADERS = [
  'Serial No',
  'Date of Booking Month',
  'Code',
  'Booking Code',
  'Agency Name / Customer Name',
  'Nationality',
  'PAX Conf.',
  'Arrival',
  'Departure',
  'Hotel Name',
  'Status',
] as const;

export const ARRIVAL_DEPARTURE_COLUMNS = [
  'Booking #',
  'Names',
  'Pax',
  'Hotel',
  'Arr/Dep',
  'Flight',
  'From',
  'To',
  'Flight Time',
  'Date',
  'Coordinator',
  'Car',
  'Driver Name',
  'Time',
] as const;

export const TOUR_COLUMNS = [
  'Booking #',
  'Names',
  'Pax',
  'Hotel',
  'Tour',
  'Guide',
  'INC',
  'OPT',
  'Pickup Time',
  'Coordinator',
  'Car',
  'Driver Name',
  'Time',
] as const;

export function formatReportRangeLabel(from: string, to: string): string {
  const start = formatOpsServiceDate(from);
  const end = formatOpsServiceDate(to);
  return from === to ? start : `${start} – ${end}`;
}

export function splitOperationReportRows(rows: OperationReportRow[]): {
  arrival: OperationReportRow[];
  tours: OperationReportRow[];
  other: OperationReportRow[];
} {
  return {
    arrival: rows.filter((row) => row.Section === 'Arrival-Departure'),
    tours: rows.filter((row) => row.Section === 'Tours'),
    other: rows.filter((row) => row.Section !== 'Arrival-Departure' && row.Section !== 'Tours'),
  };
}

export function reportCsvFilename(kind: ReportKind, from: string, to: string): string {
  return `${reportFilenameBase(kind, from, to)}.csv`;
}

export function reportExcelFilename(kind: ReportKind, from: string, to: string): string {
  return `${reportFilenameBase(kind, from, to)}.xlsx`;
}

function reportFilenameBase(kind: ReportKind, from: string, to: string): string {
  const range = from === to ? from : `${from}_to_${to}`;
  const prefix =
    kind === 'operations'
      ? 'NGK-Tours-Operation-List'
      : kind === 'passengers'
        ? 'NGK-Tours-Passengers-List'
        : 'NGK-Tours-Summary';
  return `${prefix}_${range}`;
}

function csvTitleBlock(title: string, from: string, to: string): string[][] {
  const generated = new Date().toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return [
    ['NGK Tours'],
    [title],
    [`Date: ${formatReportRangeLabel(from, to)}`],
    [`Generated: ${generated}`],
    [],
  ];
}

function rowValues(row: OperationReportRow, columns: readonly string[]): string[] {
  return columns.map((column) => String((row as unknown as Record<string, string | number>)[column] ?? ''));
}

export function buildOperationCsvData(
  rows: OperationReportRow[],
  from: string,
  to: string
): string[][] {
  const { arrival, tours, other } = splitOperationReportRows(rows);
  const data = csvTitleBlock('Operation List', from, to);

  const pushSection = (
    heading: string,
    columns: readonly string[],
    sectionRows: OperationReportRow[]
  ) => {
    if (!sectionRows.length) return;
    data.push([heading]);
    data.push([...columns]);
    sectionRows.forEach((row) => data.push(rowValues(row, columns)));
    data.push([]);
  };

  pushSection('ARRIVAL / DEPARTURE', ARRIVAL_DEPARTURE_COLUMNS, arrival);
  pushSection('TOURS', TOUR_COLUMNS, tours);
  pushSection('OTHER SERVICES', OPERATION_CSV_HEADERS, other);

  return data;
}

export function buildPassengerCsvData(
  rows: PassengerReportRow[],
  from: string,
  to: string
): Array<Array<string | number>> {
  const data: Array<Array<string | number>> = csvTitleBlock('Passengers List', from, to);
  data.push([...PASSENGER_CSV_HEADERS]);
  rows.forEach((row) => {
    data.push(PASSENGER_CSV_HEADERS.map((column) => (row as unknown as Record<string, string | number>)[column] ?? ''));
  });
  return data;
}

export function buildSummaryCsvData(
  rows: SummaryReportRow[],
  from: string,
  to: string
): Array<Array<string | number>> {
  const data: Array<Array<string | number>> = csvTitleBlock('Summary', from, to);
  data.push([...SUMMARY_CSV_HEADERS]);
  rows.forEach((row) => {
    data.push(
      SUMMARY_CSV_HEADERS.map(
        (column) => (row as unknown as Record<string, string | number>)[column] ?? ''
      )
    );
  });
  return data;
}
